/**
 * useModalA11y — WCAG AA modal accessibility for hand-rolled overlays that
 * cannot cheaply migrate to the shared Radix Dialog primitive
 * (`@/components/ui/dialog`). The primitive should be preferred; this hook
 * exists only for bespoke overlays/drawers/slide-ins so they stop reinventing
 * (and omitting) modal semantics.
 *
 * What it provides when `open` is true:
 *  - Focus moves INTO the dialog on open (first focusable, or `initialFocusRef`).
 *  - Focus is TRAPPED inside the container (Tab / Shift+Tab cycle).
 *  - Focus RESTORES to the previously-focused element (the trigger) on close.
 *  - Escape closes (unless `dismissable === false` for blocking gates).
 *  - Background scroll is locked while open.
 *
 * Pair with `dialogA11yProps()` to apply role="dialog" + aria-modal + the
 * label/description wiring on the container element.
 */

import { useEffect, useRef, type RefObject } from "react";

interface UseModalA11yOptions {
  open: boolean;
  onClose: () => void;
  /** Set false for non-dismissable blocking gates (NDA/onboarding). Escape + restore-on-close still skipped for Escape only. */
  dismissable?: boolean;
  /** Element to focus on open. Defaults to the first focusable inside the container. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useModalA11y<T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  dismissable = true,
  initialFocusRef,
}: UseModalA11yOptions): RefObject<T | null> {
  const containerRef = useRef<T | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Scroll-lock + focus-restore lifecycle.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus into the dialog on open.
    const focusTimer = window.setTimeout(() => {
      const node = containerRef.current;
      if (!node) return;
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
        return;
      }
      const focusable = node.querySelectorAll<HTMLElement>(FOCUSABLE);
      (focusable[0] ?? node).focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the trigger that opened the dialog.
      previouslyFocused.current?.focus?.();
    };
  }, [open, initialFocusRef]);

  // Focus-trap + Escape.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissable) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const node = containerRef.current;
      if (!node) return;
      const focusable = Array.from(
        node.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !node.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onClose, dismissable]);

  return containerRef;
}

/**
 * dialogA11yProps — spread onto a hand-rolled modal container to apply the
 * required WCAG dialog semantics. Provide EITHER `labelledById` (preferred,
 * points at a visible title element's id) OR `label` (an accessible name when
 * there's no visible title). `describedById` points at the body text.
 */
export function dialogA11yProps(opts: {
  labelledById?: string;
  label?: string;
  describedById?: string;
}): {
  role: "dialog";
  "aria-modal": true;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  tabIndex: -1;
} {
  return {
    role: "dialog",
    "aria-modal": true,
    "aria-labelledby": opts.labelledById,
    "aria-label": opts.labelledById ? undefined : opts.label,
    "aria-describedby": opts.describedById,
    tabIndex: -1,
  };
}
